import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { useToast } from '@kanda-libs/ks-design-library';
import type { CustomerOptions } from '@kanda-libs/ks-frontend-services';
import { FinanceRate, actions } from '@kanda-libs/ks-frontend-services';

import { useAppDispatch } from 'components/shared/App';
import jobSelectors from 'store/selectors/job';
import paymentSelectors from 'store/selectors/payments';

import useApiError from './useApiError';

export type OverrideJobSuccessCallback = () => void;

export type CallbackType =
  | 'approve'
  | 'decline'
  | 'refer'
  | 'cancel'
  | 'action';

export interface OverrideJobHook {
  isSubmitting: boolean;
  overrideJob: (
    id: string,
    xref: string,
    callback: CallbackType,
    successCallback: OverrideJobSuccessCallback,
    newRate?: FinanceRate,
    deposit?: number,
    requireRate?: boolean,
  ) => void;
}

export default function useOverrideJob(): OverrideJobHook {
  const paymentsisSubmitting = useSelector(paymentSelectors.getIsSubmitting);
  const jobsIsSubmitting = useSelector(jobSelectors.getIsSubmitting);

  const isSubmitting = paymentsisSubmitting || jobsIsSubmitting;

  const dispatch = useAppDispatch();
  const { showSuccess, showError } = useToast();
  const handleApiError = useApiError();

  const markAccepted = useCallback(
    (
      id: string,
      xref: string,
      successCallback: OverrideJobSuccessCallback,
      newRate?: FinanceRate,
      deposit?: number,
      requireRate = false,
    ) => {
      if (!requireRate) {
        dispatch(
          actions.overrideJob({
            body: {
              action: 'mark_job_as_accepted',
            },
            params: { id },
            onError: (error) => {
              handleApiError(
                error || 'Error overriding job and finance status',
              );
            },
            onSuccess: () => {
              showSuccess('Job and finance approved');
              successCallback();
            },
          }),
        );
        return;
      }
      if (!newRate && requireRate) {
        showError('Please select a rate');
        return;
      }

      const initialOptions = {
        xref,
        customer_options: {
          finance_option: newRate as FinanceRate,
          checkout_option:
            'apply_for_finance' as CustomerOptions['checkout_option'],
          deposit_value: {
            amount: deposit || 0,
          },
        },
      };

      dispatch(
        actions.overrideJob({
          body: {
            action: 'mark_job_as_accepted',
            ...initialOptions,
          },
          params: { id },
          onError: (error) => {
            handleApiError(error || 'Error overriding job and finance status');
          },
          onSuccess: () => {
            showSuccess('Job and finance approved');
            successCallback();
          },
        }),
      );
    },
    [actions, showError, showSuccess, dispatch],
  );

  const markDeclined = useCallback(
    (id: string, successCallback: OverrideJobSuccessCallback) => {
      dispatch(
        actions.overrideJob({
          body: { action: 'mark_job_as_declined' },
          params: { id },
          onError: () => {
            showError('Error overriding job and finance status');
          },
          onSuccess: () => {
            showSuccess('Job and finance declined');
            successCallback();
          },
        }),
      );
    },
    [actions.overrideJob, showError, showSuccess],
  );

  const markCancelled = useCallback(
    (id: string, successCallback: OverrideJobSuccessCallback) => {
      dispatch(
        actions.overrideJob({
          body: { action: 'mark_job_as_cancelled' },
          params: { id },
          onError: () => {
            showError('Error overriding job and finance status');
          },
          onSuccess: () => {
            showSuccess('Job and finance cancelled');
            successCallback();
          },
        }),
      );
    },
    [actions.overrideJob, showError, showSuccess],
  );

  const markReferred = useCallback(
    (id: string, successCallback: OverrideJobSuccessCallback) => {
      dispatch(
        actions.overrideJob({
          body: { action: 'mark_job_as_referred' },
          params: { id },
          onError: () => {
            showError('Error overriding job and finance status');
          },
          onSuccess: () => {
            showSuccess('Job and finance referred');
            successCallback();
          },
        }),
      );
    },
    [actions.overrideJob, showError, showSuccess],
  );

  const markActionCustomer = useCallback(
    (id: string, successCallback: OverrideJobSuccessCallback) => {
      dispatch(
        actions.overrideJob({
          body: { action: 'mark_job_as_action_customer' },
          params: { id },
          onError: () => {
            showError('Error overriding job and finance status');
          },
          onSuccess: () => {
            showSuccess(
              'Job and finance marked as action required by customer',
            );
            successCallback();
          },
        }),
      );
    },
    [actions.overrideJob, showError, showSuccess],
  );

  const overrideJob = useCallback(
    (
      id: string,
      xref: string,
      callback: CallbackType,
      successCallback: OverrideJobSuccessCallback,
      newRate?: FinanceRate,
      deposit?: number,
      requireRate = false,
    ) => {
      if (callback === 'action') {
        markActionCustomer(id, successCallback);
        return;
      }
      if (callback === 'cancel') {
        markCancelled(id, successCallback);
        return;
      }
      if (callback === 'refer') {
        markReferred(id, successCallback);
        return;
      }
      if (callback === 'decline') {
        markDeclined(id, successCallback);
        return;
      }
      if (callback === 'approve') {
        markAccepted(id, xref, successCallback, newRate, deposit, requireRate);
        return;
      }
    },
    [],
  );

  return {
    isSubmitting,
    overrideJob,
  };
}
